import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../../api/client'
import { ApiError } from '../../api/problem'
import { sseData, streamEvents } from '../../api/sse'
import { getAccessToken } from '../../auth/tokenStore'
import { describeApiFailure } from '../../lib/failure'
import { interpretJobLookupFailure, isTerminal, type GenerationJob } from './types'

/** How often the polling fallback re-reads a job. Matches the server's own watch interval. */
const POLL_INTERVAL_MS = 1000

export interface GenerationJobOptions {
  /** Starts the job. The `202` body is the job itself, in the same shape every read uses. */
  start: () => Promise<GenerationJob>
  /**
   * Runs exactly once when the job reaches a terminal state *or* turns out to have been
   * swept. Refetch the owning sheet here — for the image kinds the result is a pointer to
   * something already saved and linked, not the payload.
   */
  onSettled?: (job: GenerationJob | null) => void
}

export interface GenerationJobState {
  /** The last state the server reported. Null before the `202` lands, or once swept. */
  job: GenerationJob | null
  /** True from the moment `run` is called until the job settles. */
  isRunning: boolean
  /** The job is gone from the store — assume it finished. See `interpretJobLookupFailure`. */
  assumedFinished: boolean
  /** A real failure: the request to start it, or a lookup that was not a `404`. */
  failure: string | null
  run: () => void
  cancel: () => void
  /** Clears a settled job so the card goes back to offering the action. */
  dismiss: () => void
}

/**
 * Drives one generation job from `202` to done.
 *
 * The flow is the API's: `POST …/generate` answers `202` with a job, `GET /jobs/{id}/events`
 * streams a `status` event on every transition and then `done`. An already-finished job
 * still gets one of each before the server closes, so subscribing late is safe.
 *
 * **Three rules live here so they live in one place:**
 *
 * 1. **A `404` means finished, not failed.** The job registry is in-memory and swept after
 *    thirty minutes, and a successful job persists its output *before* reporting success.
 *    Both the subscribe and the poll route their errors through
 *    `interpretJobLookupFailure`, and a `finished` verdict settles the job normally.
 * 2. **SSE, with polling as the fallback.** A proxy that buffers `text/event-stream`, or a
 *    network that drops it, must not leave the screen stuck on "generating" forever — so a
 *    stream that fails before reaching a terminal state hands over to a poll rather than
 *    surfacing an error.
 * 3. **`onSettled` fires exactly once.** Refetching a sheet twice is harmless; a caller
 *    that also navigates or shows a toast should not have to guard against a second call.
 */
export function useGenerationJob(options: GenerationJobOptions): GenerationJobState {
  const [job, setJob] = useState<GenerationJob | null>(null)
  const [isRunning, setIsRunning] = useState(false)
  const [assumedFinished, setAssumedFinished] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)

  // Refs, not state: these are read inside long-lived async loops, where a stale closure
  // over a state value would keep watching a job the user has already cancelled.
  const abort = useRef<AbortController | null>(null)
  const settled = useRef(false)

  // Both callbacks are fresh closures on every render. Held in refs so `run` stays a
  // stable function — a `run` that changed identity every render would restart nothing
  // useful and would make every caller's own memoization pointless.
  const onSettled = useRef(options.onSettled)
  onSettled.current = options.onSettled
  const start = useRef(options.start)
  start.current = options.start

  const finish = useCallback((final: GenerationJob | null, swept: boolean) => {
    if (settled.current) return
    settled.current = true

    setIsRunning(false)
    setAssumedFinished(swept)
    if (final !== null) setJob(final)
    onSettled.current?.(final)
  }, [])

  /** Reads the job until it settles. The fallback when the event stream is unavailable. */
  const poll = useCallback(
    async (jobId: string, signal: AbortSignal) => {
      while (!signal.aborted) {
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS))
        if (signal.aborted) return

        try {
          const current = await api.get<GenerationJob>(`/jobs/${jobId}`, { signal })
          setJob(current)
          if (isTerminal(current.status)) {
            finish(current, false)
            return
          }
        } catch (error) {
          if (signal.aborted) return

          if (interpretJobLookupFailure(error) === 'finished') {
            finish(null, true)
            return
          }

          setFailure(describeApiFailure(error, 'That job is not yours to watch.'))
          setIsRunning(false)
          return
        }
      }
    },
    [finish],
  )

  const watch = useCallback(
    async (jobId: string, signal: AbortSignal) => {
      try {
        // The stream needs the bearer explicitly: `EventSource` cannot set headers, so this
        // reads the body by hand (api/sse.ts) and must supply the token itself.
        for await (const event of streamEvents(`/jobs/${jobId}/events`, {
          accessToken: getAccessToken(),
          signal,
        })) {
          const payload = sseData<GenerationJob>(event)
          if (payload !== null) setJob(payload)

          if (event.event === 'done') {
            finish(payload, false)
            return
          }
        }

        // The stream ended without a `done`. Whatever happened, the job's own state is the
        // authority — so fall through to a poll rather than guessing.
        if (!signal.aborted && !settled.current) void poll(jobId, signal)
      } catch (error) {
        if (signal.aborted) return

        if (interpretJobLookupFailure(error) === 'finished') {
          finish(null, true)
          return
        }

        // Anything else — a buffering proxy, a dropped connection — is a reason to poll,
        // not a reason to tell the user the generation failed. It very probably has not.
        void poll(jobId, signal)
      }
    },
    [finish, poll],
  )

  const run = useCallback(() => {
    abort.current?.abort()
    const controller = new AbortController()
    abort.current = controller

    settled.current = false
    setJob(null)
    setAssumedFinished(false)
    setFailure(null)
    setIsRunning(true)

    void (async () => {
      let started: GenerationJob
      try {
        started = await start.current()
      } catch (error) {
        setIsRunning(false)
        // 503 is the one worth its own sentence: it means the AI service is not configured
        // or is refusing work, which is nothing the user did.
        setFailure(
          error instanceof ApiError && error.status === 503
            ? 'The generator is unavailable right now. Try again shortly.'
            : describeApiFailure(error, 'Only the owner can generate this.'),
        )
        return
      }

      setJob(started)
      if (isTerminal(started.status)) {
        finish(started, false)
        return
      }
      if (started.jobId === undefined) {
        // No id to watch. The work was accepted, so treat it as done and let the refetch
        // say what actually happened.
        finish(started, true)
        return
      }

      void watch(started.jobId, controller.signal)
    })()
  }, [finish, watch])

  const cancel = useCallback(() => {
    const jobId = job?.jobId
    abort.current?.abort()

    if (jobId !== undefined && !settled.current) {
      // Best effort: the API answers 409 for a job that already wrote its result, which is
      // not a failure worth reporting — cancellation is not a rollback.
      void api.delete(`/jobs/${jobId}`).catch(() => undefined)
    }

    settled.current = true
    setIsRunning(false)
  }, [job?.jobId])

  const dismiss = useCallback(() => {
    setJob(null)
    setAssumedFinished(false)
    setFailure(null)
  }, [])

  // A screen that unmounts mid-generation must not leave a reader or a poll loop running.
  // The job itself carries on server-side; only this client's watching stops.
  useEffect(() => () => abort.current?.abort(), [])

  return { job, isRunning, assumedFinished, failure, run, cancel, dismiss }
}
